import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  Area,
  AreaChart,
} from "recharts";
import { ArrowUp, ArrowDown, Minus, Lock } from "lucide-react";
import { BRANDS } from "../lib/data";
import Reveal from "../components/Reveal";
import AdminPanel from "../components/AdminPanel";
import LiveRatesTicker from "../components/LiveRatesTicker";
import SteelWeightCalculator from "../components/SteelWeightCalculator";
import { formatPrice } from "../lib/price";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 6 brands — SHRISHTII/SUGNA removed.
const LINE_COLORS = {
  RADHA_550: "#e85a1c",
  SHREE: "#7c3aed",
  RADHA_RHINO_600: "#0891b2",
  SARWOTTAM: "#16a34a",
  JAIRAJ: "#dc2626",
  TATA: "#1c1c1e",
};

const HISTORY_KEYS = Object.keys(LINE_COLORS);

export default function Price() {
  const [history, setHistory] = useState([]);
  const [prices, setPrices] = useState([]);
  const [selected, setSelected] = useState(["RADHA_550", "TATA", "JAIRAJ"]);
  const [adminOpen, setAdminOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadData = useCallback(async () => {
    try {
      const [h, p] = await Promise.all([
        axios.get(`${API}/prices/history?days=30`),
        axios.get(`${API}/prices`),
      ]);
      setHistory(h.data);
      setPrices(p.data);
    } catch (err) {
      console.warn("[Price] /api/prices or /api/prices/history fetch failed:", err);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleLine = (key) =>
    setSelected((s) =>
      s.includes(key) ? s.filter((x) => x !== key) : [...s, key]
    );

  return (
    <div data-testid="price-page" className="grain bg-[#f7f7f5]">
      {/* Live rates ticker — directly below navbar */}
      <LiveRatesTicker refreshKey={refreshKey} />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-black/5 bg-gradient-to-b from-[#f7f7f5] to-white py-24">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="absolute -left-32 top-1/2 h-[500px] w-[500px] -translate-y-1/2 rounded-full bg-[#fff1e8] opacity-50 blur-3xl" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal>
            <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
              / Live TMT pricing
            </div>
            <h1 className="mt-5 font-display text-5xl uppercase leading-[0.9] tracking-tighter text-[#1c1c1e] md:text-7xl lg:text-[88px]">
              Real prices.<br />
              <span className="text-[#e85a1c]">Real tonnage.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#4a4a4e]">
              Refreshed every minute. Pricing per kilogram.
              For a binding quote, send us an enquiry.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Today's prices */}
      <section className="border-b border-black/5 bg-white py-16">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal className="flex items-end justify-between gap-4">
            <div>
              <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
                Today
              </div>
              <h2 className="mt-3 font-display text-3xl uppercase tracking-tighter text-[#1c1c1e] md:text-4xl">
                Live Brand Rates
              </h2>
            </div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8b8b8f]">
              ₹ per kg
            </div>
          </Reveal>

          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            {(prices.length
              ? prices
              : BRANDS.map((b) => ({ brand: b.name, price: b.price, change: 0, trend: "flat" }))
            ).map((p) => (
              <div
                key={p.brand}
                data-testid={`price-card-${p.brand.replace(/\s+/g, "-").toLowerCase()}`}
                className="group rounded-2xl border border-black/5 bg-white p-5 shadow-lux transition-all duration-300 hover:-translate-y-1 hover:shadow-lux-hover"
              >
                <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">
                  {p.brand}
                </div>
                <div
                  data-testid={`price-value-${p.brand.replace(/\s+/g, "-").toLowerCase()}`}
                  className="mt-3 font-display text-3xl tracking-tighter text-[#1c1c1e]"
                >
                  ₹{formatPrice(p.price)}
                </div>
                <div
                  data-testid={`price-change-${p.brand.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono-industrial text-[10px] font-semibold ${
                    p.trend === "up"
                      ? "bg-emerald-100 text-emerald-800"
                      : p.trend === "down"
                        ? "bg-red-100 text-red-800"
                        : "bg-black/5 text-[#8b8b8f]"
                  }`}
                >
                  {p.trend === "up" ? <ArrowUp size={11} /> : p.trend === "down" ? <ArrowDown size={11} /> : <Minus size={11} />}
                  {p.change > 0 ? "+" : ""}
                  {p.change}%
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steel Weight Calculator — premium builder-friendly */}
      <section className="border-b border-black/5 bg-[#f7f7f5] py-20" data-testid="calculator-section">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
          <SteelWeightCalculator />
        </div>
      </section>

      {/* Price history graph */}
      <section className="border-b border-black/5 bg-[#f7f7f5] py-16">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal
            delay={100}
            data-testid="price-graph"
            className="rounded-2xl border border-black/5 bg-white p-7 shadow-lux md:p-8"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
                  / 30-day price history
                </div>
                <h3 className="mt-3 font-display text-3xl uppercase tracking-tighter text-[#1c1c1e]">
                  Brand price trends
                </h3>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              {HISTORY_KEYS.map((k) => (
                <button
                  key={k}
                  onClick={() => toggleLine(k)}
                  data-testid={`toggle-${k}`}
                  className={`rounded-full border px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] transition-all ${
                    selected.includes(k)
                      ? "border-black/15 bg-white"
                      : "border-black/5 bg-[#f7f7f5] text-[#8b8b8f] hover:text-[#1c1c1e]"
                  }`}
                  style={{
                    color: selected.includes(k) ? LINE_COLORS[k] : undefined,
                  }}
                >
                  {k.replace(/_/g, " ")}
                </button>
              ))}
            </div>

            <div className="mt-5 h-[380px]">
              <ResponsiveContainer width="100%" height="100%">
                {selected.length === 1 ? (
                  <AreaChart data={history}>
                    <defs>
                      <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={LINE_COLORS[selected[0]]} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={LINE_COLORS[selected[0]]} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="rgba(28,28,30,0.06)" />
                    <XAxis dataKey="date" stroke="#8b8b8f" tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis stroke="#8b8b8f" tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }} domain={["auto", "auto"]} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid rgba(28,28,30,0.1)", borderRadius: 8, fontFamily: "JetBrains Mono", fontSize: 12 }} />
                    <Area type="monotone" dataKey={selected[0]} stroke={LINE_COLORS[selected[0]]} strokeWidth={2.5} fill="url(#areaGrad)" />
                  </AreaChart>
                ) : (
                  <LineChart data={history}>
                    <CartesianGrid stroke="rgba(28,28,30,0.06)" />
                    <XAxis dataKey="date" stroke="#8b8b8f" tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis stroke="#8b8b8f" tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }} domain={["auto", "auto"]} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid rgba(28,28,30,0.1)", borderRadius: 8, fontFamily: "JetBrains Mono", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 10 }} />
                    {selected.map((k) => (
                      <Line key={k} type="monotone" dataKey={k} stroke={LINE_COLORS[k]} strokeWidth={2} dot={false} activeDot={{ r: 4, fill: "#e85a1c" }} />
                    ))}
                  </LineChart>
                )}
              </ResponsiveContainer>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Discreet admin trigger */}
      <section className="bg-[#f7f7f5]">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-8 lg:px-10">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8b8b8f]">
            Prices update every minute · Source: in-house desk
          </div>
          <button
            data-testid="admin-access-btn"
            onClick={() => setAdminOpen(true)}
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-[#8b8b8f] transition-all duration-300 hover:text-[#e85a1c]"
          >
            <Lock size={11} /> Admin Access
          </button>
        </div>
      </section>

      <AdminPanel
        open={adminOpen}
        onClose={() => setAdminOpen(false)}
        onChange={() => {
          loadData();
          setRefreshKey((k) => k + 1);
        }}
      />
    </div>
  );
}
